/**
 * useChampionshipToggleInjector Hook
 * 
 * Runtime DOM injection of "Enter Championship" toggle into SystemMenu.
 * Uses vanilla DOM manipulation to avoid React reconciliation.
 * Includes loading state to prevent double-taps.
 */

import { useEffect, useRef } from 'react';
import { trpc } from '@/lib/trpc';

export function useChampionshipToggleInjector(): void {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const toggleMutation = trpc.admin.toggleRaffleOptIn.useMutation();
  const meQuery = trpc.auth.me.useQuery();

  useEffect(() => {
    // Wait for auth data to be available
    if (!meQuery.data) return;

    const injectToggle = () => {
      // Check if toggle already exists
      if (document.getElementById('championship-toggle-container')) {
        return;
      }

      // Find SystemMenu modal content
      const modalContent = document.querySelector('[class*="SystemMenu"]');
      if (!modalContent) {
        return;
      }

      // Create toggle container
      const container = document.createElement('div');
      container.id = 'championship-toggle-container';
      container.style.cssText = `
        padding: 16px;
        border-top: 1px solid #e5e7eb;
        border-bottom: 1px solid #e5e7eb;
        background: #f9fafb;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
      `;

      // Create label
      const label = document.createElement('div');
      label.style.cssText = `
        display: flex;
        flex-direction: column;
        gap: 4px;
      `;

      const title = document.createElement('div');
      title.textContent = '🏆 Enter Championship';
      title.style.cssText = `
        font-weight: 600;
        font-size: 14px;
        color: #1f2937;
        font-family: system-ui, -apple-system, Roboto, Arial, sans-serif;
      `;

      const subtitle = document.createElement('div');
      subtitle.textContent = 'Opt-in to win the prize';
      subtitle.style.cssText = `
        font-size: 12px;
        color: #6b7280;
        font-family: system-ui, -apple-system, Roboto, Arial, sans-serif;
      `;

      label.appendChild(title);
      label.appendChild(subtitle);

      // Create toggle switch
      const toggleSwitch = document.createElement('button');
      toggleSwitch.id = 'championship-toggle-switch';
      toggleSwitch.style.cssText = `
        position: relative;
        width: 48px;
        height: 28px;
        background: ${meQuery.data?.raffleOptIn ? '#10b981' : '#d1d5db'};
        border: none;
        border-radius: 14px;
        cursor: pointer;
        transition: background 0.3s ease;
        flex-shrink: 0;
      `;

      // Create toggle knob
      const knob = document.createElement('div');
      knob.style.cssText = `
        position: absolute;
        top: 2px;
        left: ${meQuery.data?.raffleOptIn ? '24px' : '2px'};
        width: 24px;
        height: 24px;
        background: white;
        border-radius: 12px;
        transition: left 0.3s ease;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
      `;
      toggleSwitch.appendChild(knob);

      // Handle toggle click
      toggleSwitch.addEventListener('click', async () => {
        const isCurrentlyOptedIn = meQuery.data?.raffleOptIn ?? false;
        const newOptInState = !isCurrentlyOptedIn;

        // Set loading state
        toggleSwitch.disabled = true;
        toggleSwitch.style.opacity = '0.6';

        try {
          await toggleMutation.mutateAsync({ optIn: newOptInState });

          // Update UI
          toggleSwitch.style.background = newOptInState ? '#10b981' : '#d1d5db';
          knob.style.left = newOptInState ? '24px' : '2px';

          // Update meQuery cache
          meQuery.refetch();

          console.log(`[ChampionshipToggle] Opt-in toggled to: ${newOptInState}`);
        } catch (error) {
          console.error('[ChampionshipToggle] Error toggling opt-in:', error);
          // Revert UI on error
          toggleSwitch.style.background = isCurrentlyOptedIn ? '#10b981' : '#d1d5db';
          knob.style.left = isCurrentlyOptedIn ? '24px' : '2px';
        } finally {
          toggleSwitch.disabled = false;
          toggleSwitch.style.opacity = '1';
        }
      });

      container.appendChild(label);
      container.appendChild(toggleSwitch);

      // Inject into modal (prepend so it appears near top)
      const firstChild = modalContent.firstChild;
      if (firstChild) {
        modalContent.insertBefore(container, firstChild);
      } else {
        modalContent.appendChild(container);
      }

      console.log('[ChampionshipToggle] Toggle injected into SystemMenu');
    };

    injectToggle();
  }, [meQuery.data, toggleMutation]);
}
